import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import AIChat from '../ai/AIChat';
import LicenseBanner from '../shared/LicenseBanner';
import { useIsDesktop } from '../../hooks/useMediaQuery';

const MainLayout = ({ children }) => {
  const [showAI, setShowAI] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const isDesktop = useIsDesktop();
  const location = useLocation();

  // Collapse is a desktop-only concept; on mobile the drawer always shows full labels.
  const effectiveCollapsed = isDesktop && isSidebarCollapsed;

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setIsMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleGlobalShortcut = (event) => {
      if (!event.ctrlKey || event.key.toLowerCase() !== 'q') return;

      const target = event.target;
      const isEditable = target instanceof HTMLElement && (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable
      );

      if (isEditable) return;

      event.preventDefault();
      setShowAI(true);
    };

    window.addEventListener('keydown', handleGlobalShortcut);
    return () => window.removeEventListener('keydown', handleGlobalShortcut);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-slate-950 dark:text-slate-100">
      <Sidebar
        isCollapsed={effectiveCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed((prev) => !prev)}
        isMobileOpen={isMobileNavOpen}
        onMobileClose={() => setIsMobileNavOpen(false)}
      />
      {/* Mobile drawer backdrop */}
      {isMobileNavOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/50 lg:hidden"
          onClick={() => setIsMobileNavOpen(false)}
          aria-hidden="true"
        />
      )}
      <div className={`ml-0 ${isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'} flex flex-col min-h-screen transition-all duration-300`}>
        <Header
          onToggleAI={() => setShowAI(!showAI)}
          isSidebarCollapsed={effectiveCollapsed}
          onOpenMobileNav={() => setIsMobileNavOpen(true)}
        />
        {/* Mounted here rather than in App.js so it can only ever appear inside
            the authenticated shell — never on /login or /register. */}
        <LicenseBanner />
        <main className={`layout-main flex-1 transition-all duration-300 ${isSidebarCollapsed ? 'layout-roomy px-4 py-4 lg:px-8 lg:py-8 xl:px-10' : 'p-4 lg:p-6'}`}>
          {children}
        </main>
      </div>
      {showAI && (
        <AIChat
          onClose={() => setShowAI(false)}
          currentPage={location.pathname}
          isSidebarCollapsed={isSidebarCollapsed}
        />
      )}
    </div>
  );
};

export default MainLayout;
